import { Fragment, useState } from "react";
import {
	Box,
	Divider,
	IconButton,
	Paper,
	Stack,
	Switch,
	Tooltip,
	Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
	ErrorOutlineRounded,
	ReplayRounded,
	TuneOutlined,
	WarningAmberRounded,
} from "@mui/icons-material";
import {
	LABEL_CATEGORIA_INTELIGENTE,
	LABEL_SEVERIDADE,
} from "../../../../utils/centralDeAlertas/labels";
import { corCategoriaInteligente } from "../../../../utils/centralDeAlertas/cores";
import { GUIDES_CENTRAL, MetricInfo } from "../../metricInfo";
import ModalCategoriaInteligente from "../modalCategoriaInteligente";
import useStyles from "./styles";

// Aba "Inteligentes" da tela de config: um Paper com uma LINHA por detecção
// automática do painel (zona morta, OS faltante, falha recorrente), no mesmo
// idioma da aba Exibição — switch inline com rótulo de estado e lápis de
// ajustes, sem menu de overflow. A linha mostra o estado e a regra em vigor;
// a edição dos parâmetros acontece no modal (só nas categorias que têm
// parâmetros). Categoria desativada some da lista de Alertas inteligentes e
// das categorias dos incidentes — os KPIs do painel ("Sem atendimento",
// "Atendimentos sem OS") NÃO são afetados.
//
// `sla_em_risco` fica de fora de propósito: a categoria não é detectada nesta
// fase (sem fonte de SLA contratual) — ver alertasInteligentes/index.js.

// Mesmos ícones da lista de Alertas inteligentes do dashboard.
const ICONE_CATEGORIA = {
	zona_morta: WarningAmberRounded,
	os_faltante: ErrorOutlineRounded,
	falha_recorrente: ReplayRounded,
};

const DESCRICAO_CATEGORIA = {
	zona_morta:
		"Alerta sem nenhum atendimento correlacionado por mais que o limiar",
	os_faltante: "Atendimento resolvido sem Ordem de Serviço vinculada",
	falha_recorrente:
		"Equipamento com alertas de indisponibilidade repetidos na janela",
};

// Observação por categoria, exibida em itálico abaixo da regra.
const NOTA_CATEGORIA = {
	falha_recorrente:
		"Alertas do tipo “Host fora do ar” (ICMP/SNMP) contam sempre, em qualquer severidade.",
};

const GUIA_CATEGORIA = {
	zona_morta: "inteligentesZonaMorta",
	os_faltante: "inteligentesOsFaltante",
	falha_recorrente: "inteligentesFalhaRecorrente",
};

// Categorias com parâmetros editáveis (as demais só ativam/desativam).
const CATEGORIAS_COM_PARAMETROS = ["zona_morta", "falha_recorrente"];

// Payload completo de salvamento a partir do estado salvo (o backend valida
// todos os campos da categoria em todo save, então o switch reenvia os
// valores vigentes).
function montarPayload(categoria, ativo) {
	const base = { chave: categoria.chave, ativo };
	if (categoria.chave === "zona_morta") {
		return { ...base, limiarMinutos: categoria.limiarMinutos ?? null };
	}
	if (categoria.chave === "falha_recorrente") {
		return {
			...base,
			minimoAlertas: categoria.minimoAlertas,
			janelaHoras: categoria.janelaHoras,
			severidadeMinima: categoria.severidadeMinima,
		};
	}
	return base;
}

// Resumo textual dos parâmetros em vigor, por categoria.
function resumosDaCategoria(categoria, janelaCorrelacaoMinutos) {
	if (categoria.chave === "zona_morta") {
		return [
			{
				rotulo: "Limiar:",
				valor:
					categoria.limiarMinutos != null
						? `${categoria.limiarMinutos} min sem atendimento`
						: `janela de correlação (${janelaCorrelacaoMinutos} min)`,
			},
		];
	}
	if (categoria.chave === "falha_recorrente") {
		return [
			{
				rotulo: "Regra:",
				valor: `${categoria.minimoAlertas} alertas em ${categoria.janelaHoras}h`,
			},
			{
				rotulo: "Severidade mínima:",
				valor: `${LABEL_SEVERIDADE[categoria.severidadeMinima] || categoria.severidadeMinima} ou pior`,
			},
		];
	}
	return [
		{ rotulo: "Regra:", valor: "sem parâmetros — a detecção é binária" },
	];
}

function LinhaCategoria({
	categoria,
	limites,
	janelaCorrelacaoMinutos,
	onSalvar,
}) {
	const theme = useTheme();
	const styles = useStyles(theme);
	const { chave, ativo } = categoria;
	const Icone = ICONE_CATEGORIA[chave];
	const cor = corCategoriaInteligente(theme, chave);
	const rotulo = LABEL_CATEGORIA_INTELIGENTE[chave] || chave;
	const editavel = CATEGORIAS_COM_PARAMETROS.includes(chave);
	const nota = NOTA_CATEGORIA[chave];

	const [modalAberto, setModalAberto] = useState(false);
	const [salvando, setSalvando] = useState(false);

	const alternarAtivo = async () => {
		setSalvando(true);
		try {
			await onSalvar(montarPayload(categoria, !ativo));
		} finally {
			setSalvando(false);
		}
	};

	return (
		<Box sx={styles.linha}>
			<Box sx={styles.conteudo(ativo)}>
				{Icone && <Icone sx={styles.iconeCategoria(cor)} />}
				<Box sx={styles.textos}>
					<Typography sx={styles.nome} noWrap>
						{rotulo}
					</Typography>
					<Typography variant="caption" sx={styles.legenda}>
						{DESCRICAO_CATEGORIA[chave]}
					</Typography>
					<Box sx={styles.linhaRegra}>
						{resumosDaCategoria(categoria, janelaCorrelacaoMinutos).map(
							(item, indice) => (
								<Fragment key={item.rotulo}>
									{indice > 0 && (
										<Typography variant="caption" sx={styles.rotuloRegra}>
											·
										</Typography>
									)}
									<Typography variant="caption" sx={styles.rotuloRegra}>
										{item.rotulo}
									</Typography>
									<Typography variant="caption" sx={styles.valorRegra}>
										{item.valor}
									</Typography>
								</Fragment>
							)
						)}
					</Box>
					{nota && (
						<Typography variant="caption" sx={styles.nota}>
							{nota}
						</Typography>
					)}
				</Box>
			</Box>

			<Box sx={styles.controles}>
				<MetricInfo theme={theme} guide={GUIDES_CENTRAL[GUIA_CATEGORIA[chave]]} />
				{editavel ? (
					<Tooltip title="Editar parâmetros">
						<IconButton
							size="small"
							aria-label={`Editar parâmetros de ${rotulo}`}
							onClick={() => setModalAberto(true)}
						>
							<TuneOutlined fontSize="small" />
						</IconButton>
					</Tooltip>
				) : (
					<Box sx={styles.espacoLapis} />
				)}
				<Box sx={styles.blocoSwitch}>
					<Switch
						size="small"
						checked={ativo}
						disabled={salvando}
						onChange={alternarAtivo}
						inputProps={{ "aria-label": `Detecção ${rotulo}` }}
					/>
					<Typography variant="caption" sx={styles.estado(ativo)}>
						{ativo ? "Ativa" : "Inativa"}
					</Typography>
				</Box>
			</Box>

			<ModalCategoriaInteligente
				open={modalAberto}
				onClose={() => setModalAberto(false)}
				categoria={categoria}
				limites={limites}
				janelaCorrelacaoMinutos={janelaCorrelacaoMinutos}
				onSalvar={onSalvar}
			/>
		</Box>
	);
}

export default function AbaInteligentes({ config, onSalvar }) {
	const theme = useTheme();
	const styles = useStyles(theme);

	if (!config?.categorias?.length) {
		return (
			<Typography variant="caption" sx={styles.observacao}>
				Não foi possível carregar as categorias inteligentes — recarregue a
				página.
			</Typography>
		);
	}

	return (
		<Paper
			sx={styles.container}
			variant={theme.palette.mode === "dark" ? "outlined" : "elevation"}
			elevation={theme.palette.mode === "dark" ? 0 : 3}
		>
			<Typography variant="mySubtitle">Detecções automáticas</Typography>
			<Typography variant="body2" sx={styles.intro}>
				O que alimenta o bloco Alertas inteligentes do painel. Desativar tira a
				detecção da lista e das categorias dos incidentes — os KPIs "Sem
				atendimento" e "Atendimentos sem OS" não são afetados. Os parâmetros de
				cada detecção são editados no ícone de ajustes.
			</Typography>
			<Divider sx={styles.divisorTopo} />

			<Stack divider={<Divider flexItem />} spacing={0}>
				{config.categorias.map((categoria) => (
					<LinhaCategoria
						key={categoria.chave}
						categoria={categoria}
						limites={config.limites}
						janelaCorrelacaoMinutos={config.janelaCorrelacaoMinutos}
						onSalvar={onSalvar}
					/>
				))}
			</Stack>
		</Paper>
	);
}
